import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Container } from '@/components/layout/Container';
import { RouteMap } from '@/components/map/RouteMap';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Rating } from '@/components/ui/Rating';
import { ErrorState, LoadingState } from '@/components/ui/States';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useAuth } from '@/features/auth/AuthProvider';
import { BookRideForm } from '@/features/bookings/components/BookRideForm';
import { useRide } from '@/features/rides/hooks/useRides';
import { formatDateTime, formatMoney } from '@/utils/format';
import { resolvePlace } from '@/utils/places';

const PREFS: Array<[keyof import('@/types/models').RidePreferences, string]> = [
  ['smoking', 'Smoking allowed'], ['pets', 'Pets allowed'], ['music', 'Music'], ['maxTwoInBack', 'Max two in the back'],
];

export function RideDetailPage() {
  const { rideId = '' } = useParams();
  // Carried from the search result so the leg quoted and booked here is the leg the card
  // advertised. Absent on a direct link, which books the whole route.
  const [params] = useSearchParams();
  const pickup = resolvePlace(params.get('pickup') ?? '');
  const drop = resolvePlace(params.get('drop') ?? '');
  const segment = pickup && drop ? { pickup, drop } : undefined;
  const { status, user } = useAuth();
  const { data: ride, isPending, isError, error, refetch } = useRide(rideId);

  if (isPending) return <Container><LoadingState label="Loading ride" /></Container>;
  if (isError) return <Container><ErrorState error={error} onRetry={() => void refetch()} /></Container>;

  const isDriver = user?.userId === ride.driver.userId;
  const bookable = ride.status === 'ACTIVE' && ride.availableSeats > 0;

  return (
    <Container className="grid gap-8 lg:grid-cols-[1fr_22rem]">
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{ride.origin.shortName} to {ride.destination.shortName}</h1>
            <StatusBadge status={ride.status} />
          </div>
          <p className="mt-1 text-slate-600">{formatDateTime(ride.departureAt)} &middot; about {ride.estimatedDistanceKm} km</p>
        </div>

        <Card><CardBody className="space-y-4">
          <dl className="grid gap-4 sm:grid-cols-2">
            <div><dt className="text-xs uppercase tracking-wide text-slate-500">Pickup</dt><dd className="font-medium">{ride.origin.formattedAddress}</dd></div>
            <div><dt className="text-xs uppercase tracking-wide text-slate-500">Drop-off</dt><dd className="font-medium">{ride.destination.formattedAddress}</dd></div>
            <div><dt className="text-xs uppercase tracking-wide text-slate-500">Price per seat</dt><dd className="font-medium">{formatMoney(ride.pricePerSeatMinor, ride.currency)}</dd></div>
            <div><dt className="text-xs uppercase tracking-wide text-slate-500">Bookings</dt><dd className="font-medium">{ride.instantBooking ? 'Instant' : 'Driver reviews each request'}</dd></div>
            <div><dt className="text-xs uppercase tracking-wide text-slate-500">Seats left</dt><dd className="font-medium">{ride.availableSeats} of {ride.totalSeats}</dd></div>
          </dl>
          {ride.routePolyline.length >= 2 && (
            <RouteMap
              polyline={ride.routePolyline}
              markers={[
                { point: ride.origin, label: ride.origin.shortName },
                { point: ride.destination, label: ride.destination.shortName },
              ]}
            />
          )}
          {segment && (
            <p className="rounded-xl bg-brand-50 p-3 text-sm text-brand-900">
              You searched {segment.pickup.shortName} to {segment.drop.shortName}. You will be picked up and dropped
              along this route, and pay for that part of it.
            </p>
          )}
          {ride.notes && <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">{ride.notes}</p>}
          <ul className="flex flex-wrap gap-2" aria-label="Ride preferences">
            {PREFS.filter(([k]) => ride.preferences[k]).map(([k, label]) => (
              <li key={k} className="rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-800">{label}</li>
            ))}
          </ul>
        </CardBody></Card>

        <Card><CardBody>
          <h2 className="mb-3 font-semibold">Your driver</h2>
          <Link to={`/users/${ride.driver.userId}`} className="flex items-center gap-4 rounded-xl hover:bg-slate-50">
            <Avatar name={ride.driver.name} src={ride.driver.photoUrl} size="lg" />
            <div><p className="text-lg font-medium">{ride.driver.name}</p><Rating value={ride.driver.ratingAverage} count={ride.driver.ratingCount} /></div>
          </Link>
          {ride.driver.phone ? (
            <a href={`tel:${ride.driver.phone}`} className="mt-3 block text-sm font-medium text-brand-700 hover:underline">
              Call {ride.driver.phone}
            </a>
          ) : !isDriver && (
            <p className="mt-3 text-sm text-slate-500">The driver's number appears here once they accept your booking.</p>
          )}
        </CardBody></Card>
      </div>

      <aside>
        <Card className="sticky top-24"><CardBody>
          <p className="mb-4 text-2xl font-semibold text-brand-800">{formatMoney(ride.pricePerSeatMinor, ride.currency)} <span className="text-sm font-normal text-slate-500">per seat</span></p>
          {isDriver ? (
            <div className="space-y-3"><p className="text-sm text-slate-600">This is your ride.</p>
              <Link to="/my-rides"><Button variant="secondary" className="w-full">Manage this ride</Button></Link></div>
          ) : status !== 'authenticated' ? (
            <Link to="/login" state={{ from: `/rides/${ride.rideId}` }}><Button size="lg" className="w-full">Log in to book</Button></Link>
          ) : bookable ? (
            <BookRideForm ride={ride} segment={segment} />
          ) : (
            <p className="text-sm text-slate-600">This ride is not open for booking.</p>
          )}
        </CardBody></Card>
      </aside>
    </Container>
  );
}
